// src/services/auth.ts

import axios from "axios";
import { UserCredentials, AuthResponse } from "../types/auth";

// Set the base URL for the API
const API_URL = `${
  import.meta.env.VITE_BACKEND_URL || "http://localhost:5000"
}/api/auth/`;

// Create axios instance with interceptors
const authApi = axios.create({
  baseURL: API_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

// Add request interceptor to include auth token
authApi.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Function to log in a user
export const login = async (
  credentials: UserCredentials
): Promise<AuthResponse> => {
  const response = await authApi.post("login", credentials);
  return response.data;
};

// Function to log out a user
export const logout = async (): Promise<void> => {
  await authApi.post("logout");
};

// Function to register a new user
export const register = async (
  credentials: UserCredentials
): Promise<AuthResponse> => {
  const response = await authApi.post("register", credentials);
  return response.data;
};

// Function to verify user password
export const verifyPassword = async (
  password: string
): Promise<{ success: boolean; message?: string }> => {
  const response = await authApi.post("verify-password", { password });
  return response.data;
};

// Function to register an e-course student
export const registerECourseStudent = async (
  data: ECourseRegistrationData
): Promise<ECourseRegistrationResponse> => {
  const response = await authApi.post("register-ecourse", data);
  return response.data;
};

// Function to request password reset
export const forgotPassword = async (
  email: string
): Promise<{ success: boolean; message: string }> => {
  const response = await authApi.post("forgot-password", { email });
  return response.data;
};

// Function to reset password with token
export const resetPassword = async (
  token: string,
  password: string
): Promise<{ success: boolean; message: string }> => {
  const response = await authApi.put(`reset-password/${token}`, { password });
  return response.data;
};

// Function to get the current user's profile
export const getCurrentUser = async (): Promise<AuthResponse> => {
  const response = await authApi.get("me");
  return response.data;
};
